import clsx from "clsx";
import React from "react";
import { Grid2Props } from "@mui/material";

import { LibraryPrivacy } from "@growth-machine-llc/stridist-api-client";
import {
  JourneyAnchors,
  AllOption,
  LibraryPrivacyFilter,
  TeamLibraryPromo,
} from "../../constants";
import { CoachLibraryHeader } from "../library/CoachLibraryHeader";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { Lock } from "lucide-react";

export interface CoachLibraryHeaderFiltersBaseProps extends Grid2Props {
  query: string;
  defaultQuery?: string;
  onChangeQuery?: (query: string) => void;
  privacy?: LibraryPrivacyFilter;
  onChangePrivacy?: (privacy: LibraryPrivacyFilter) => void;
  disabledResetButton?: boolean;
  onResetFilters?: () => void;
  showResetButton?: boolean;
}

export function getLibraryPrivacyOptions(
  type: "exercise" | "program" | "component",
  isTeamAccount: boolean,
) {
  const normalizedTypeText =
    type === "exercise" ? "exercises" : `${type} templates`;

  return [
    {
      value: AllOption,
      tooltip: `All ${normalizedTypeText}`,
      label: "All",
    },
    {
      value: LibraryPrivacy.PRIVATE,
      tooltip: `Private ${normalizedTypeText} uploaded by me`,
      label: "Private",
    },
    {
      value: LibraryPrivacy.TEAM,
      tooltip: isTeamAccount
        ? `Team ${normalizedTypeText} shared with all coaches`
        : TeamLibraryPromo(normalizedTypeText),
      label: "Team",
      icon: isTeamAccount ? undefined : <Lock />,
      disabled: !isTeamAccount,
    },
    {
      value: LibraryPrivacy.PUBLIC,
      tooltip: `Public ${normalizedTypeText} uploaded by Stridist`,
      label: "Public",
    },
  ];
}

export interface CoachExerciseLibraryHeaderFiltersProps
  extends CoachLibraryHeaderFiltersBaseProps {
  isTagsRoute?: boolean;
  showFilterByType?: boolean;
}

export function CoachExerciseLibraryHeaderFilters(
  props: CoachExerciseLibraryHeaderFiltersProps,
) {
  const {
    className,
    query,
    onChangeQuery,
    privacy,
    onChangePrivacy,
    isTagsRoute = false,
    disabledResetButton,
    onResetFilters,
    showResetButton,
    showFilterByType,
    defaultQuery,
  } = props;

  const { isTeamAccount } = useCurrentUser();

  const showResetAndSearchInOneRow = showResetButton && !showFilterByType;
  const handleChangePrivacy = React.useCallback(
    (privacy: LibraryPrivacyFilter) => {
      if (onChangePrivacy) {
        onChangePrivacy(privacy);
      }
    },
    [onChangePrivacy],
  );

  return (
    <CoachLibraryHeader<LibraryPrivacyFilter>
      className={className}
      query={query}
      defaultQuery={defaultQuery || ""}
      searchPlaceholder={`Search by ${isTagsRoute ? "tags" : "exercises"}`}
      onChangeQuery={onChangeQuery}
      showActions={!isTagsRoute}
      inlineActions={showResetAndSearchInOneRow}
      toggleOptions={
        showFilterByType
          ? getLibraryPrivacyOptions("exercise", isTeamAccount)
          : undefined
      }
      toggleValue={privacy}
      onChangeToggle={handleChangePrivacy}
      toggleClassName={clsx(JourneyAnchors.EXERCISE_LIBRARY_PRIVACY_FILTER)}
      showResetButton={showResetButton}
      disabledResetButton={disabledResetButton}
      onResetFilters={onResetFilters}
      resetButtonClassName={clsx(JourneyAnchors.EXERCISE_LIBRARY_RESET_FILTERS)}
    />
  );
}
